import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Plus, Trash2, Search, Pencil, Shield } from "lucide-react";
import { handleGet, handleRemove } from "../../controllers/controller.all";
import Pagination from "../../components/ui/Pagination";
import SortableHeader from "../../components/ui/SortableHeader";

export default function AdminGlobalList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    // Pagination & Sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("prenom");
    const [sortOrder, setSortOrder] = useState("asc");
    const itemsPerPage = 20;

    const fetchAdmins = async () => {
        setLoading(true);
        await handleGet(`/user/s/all?skip=0&limit=100000`, (res) => {
            if (res?.users) {
                const globalAdmins = res.users.filter(u => u.role === "ADMIN");
                setAdmins(globalAdmins);
            }
        }, token);
        setLoading(false);
    };

    useEffect(() => {
        if(token) fetchAdmins();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleDelete = async (id) => {
        await handleRemove(`/user/${id}`, fetchAdmins, token, {
            message: "Voulez-vous vraiment supprimer cet administrateur ?"
        });
    };

    const handleSort = (key, order) => {
        setSortKey(key);
        setSortOrder(order);
    };

    const filteredAdmins = admins.filter(admin => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (admin.prenom?.toLowerCase().includes(s) || 
                admin.nom?.toLowerCase().includes(s) || 
                admin.email?.toLowerCase().includes(s) || 
                admin.tel?.toLowerCase().includes(s));
    });

    const sortedAdmins = [...filteredAdmins].sort((a, b) => {
        let valA = a[sortKey] || "";
        let valB = b[sortKey] || "";
        
        if (sortKey === "ecoles") {
             valA = a.ecoles?.length || (a.ecole ? 1 : 0);
             valB = b.ecoles?.length || (b.ecole ? 1 : 0);
        }

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    const paginatedAdmins = sortedAdmins.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Administrateurs (Super Admin)</h1>
                    <p className="text-sm text-gray-500 mt-1">Gérez tous les administrateurs du système multi-écoles.</p>
                </div>
                <Link 
                    to="/super-admin/admins/create" 
                    className="inline-flex items-center justify-center gap-2 bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 rounded-lg transition w-full sm:w-auto"
                >
                    <Plus className="h-4 w-4" /> Nouveau
                </Link>
            </div>

            {/* Search bar — always visible */}
            <div className="relative w-full text-gray-400">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" />
                <input 
                    type="text" 
                    placeholder="Rechercher..." 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                />
            </div>

            {/* Mobile cards */}
            <div className="flex flex-col gap-3 md:hidden">
                {loading ? (
                    <div className="text-center py-10 text-sm text-gray-500">Chargement...</div>
                ) : paginatedAdmins.length === 0 ? (
                    <div className="text-center py-10 text-sm text-gray-500">Aucun administrateur trouvé.</div>
                ) : (
                    paginatedAdmins.map((admin) => (
                        <div key={admin._id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                                    <Shield className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-gray-900 truncate">{admin.prenom} {admin.nom}</div>
                                    <div className="text-xs text-gray-500 truncate">{admin.tel}</div>
                                    <div className="text-xs text-gray-400 truncate">{admin.email}</div>
                                    <div className="text-xs mt-0.5">
                                        {admin.ecoles?.length > 0 ? (
                                            <span className="text-emerald-600 font-medium">{admin.ecoles.length} école(s)</span>
                                        ) : admin.ecole ? (
                                            <span className="text-emerald-600 font-medium">1 école</span>
                                        ) : (
                                            <span className="text-red-500">Aucune école</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <Link 
                                    to={`/super-admin/admins/update/${admin._id}`}
                                    className="text-emerald-600 bg-emerald-50 p-2 rounded-lg transition hover:bg-emerald-100"
                                    title="Modifier"
                                >
                                    <Pencil className="h-4 w-4" />
                                </Link>
                                <button 
                                    onClick={() => handleDelete(admin._id)}
                                    className="text-red-500 bg-red-50 p-2 rounded-lg transition hover:bg-red-100"
                                    title="Supprimer"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <SortableHeader label="Nom & Prénom" sortKey="prenom" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Contact" sortKey="tel" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Écoles Assignées" sortKey="ecoles" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : paginatedAdmins.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Aucun administrateur trouvé.</td>
                                </tr>
                            ) : (
                                paginatedAdmins.map((admin) => (
                                    <tr key={admin._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                                                    <Shield className="h-5 w-5" />
                                                </div>
                                                <div className="text-sm font-medium text-gray-900">{admin.prenom} {admin.nom}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{admin.tel}</div>
                                            <div className="text-sm text-gray-500 block truncate max-w-xs">{admin.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500">
                                                {admin.ecoles?.length > 0 ? (
                                                    <span>{admin.ecoles.length} école(s)</span>
                                                ) : admin.ecole ? (
                                                    <span>1 école</span>
                                                ) : (
                                                    <span className="text-red-500">Aucune</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <Link 
                                                to={`/super-admin/admins/update/${admin._id}`}
                                                className="text-emerald-600 hover:text-emerald-800 bg-emerald-50 p-2 rounded-lg transition inline-block mr-2"
                                                title="Modifier le profil"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Link>
                                            <button 
                                                onClick={() => handleDelete(admin._id)}
                                                className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                title="Supprimer l'administrateur"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Pagination Controls */}
            {filteredAdmins.length > itemsPerPage && (
                <Pagination 
                    currentPage={currentPage} 
                    totalItems={filteredAdmins.length} 
                    itemsPerPage={itemsPerPage} 
                    onPageChange={setCurrentPage} 
                />
            )}
        </div>
    );
}

